import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import InchargeLayout from './InchargeLayout';
import {
    FaUserTie,
    FaFileAlt,
    FaTrash,
    FaTimes
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const ShowSocChallan = () => {
    const navigate = useNavigate();
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [updateStatus, setUpdateStatus] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        const regRef = ref(db, 'ekamwatapzp/SocietyRegistrations');
        const unsubscribe = onValue(regRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                // In a real scenario, this list might be filtered based on payment logic, 
                // but we display the whole list here by default.
                setRegistrations(list);
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const filteredData = registrations.filter(item =>
        (item.societyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.chairmanWhatsapp || item.whatsappNo || '').includes(searchTerm) ||
        (item.transactionId || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved': return 'text-emerald-600';
            case 'Rejected': return 'text-rose-600';
            case 'Pending': return 'text-amber-500';
            default: return 'text-gray-600';
        }
    };

    const openModal = (item) => {
        setSelectedUser(item);
        setUpdateStatus(item.assistantStatus || '');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
        setUpdateStatus('');
    };

    const handleUpdateStatus = async () => {
        if (!updateStatus) {
            toast.warning("Status निवडा");
            return;
        }
        setIsUpdating(true);
        try {
            const userRef = ref(db, `ekamwatapzp/SocietyRegistrations/${selectedUser.id}`);
            await update(userRef, {
                assistantStatus: updateStatus,
                statusUpdatedAt: new Date().toISOString()
            });
            toast.success("स्थिती यशस्वीरित्या अपडेट केली!");
            closeModal();
        } catch (error) {
            console.error("Update Error:", error);
            toast.error("अपडेट करताना त्रुटी आली.");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = async (id) => {
        if(window.confirm("तुम्हाला खात्री आहे का की तुम्हाला ही माहिती डिलीट करायची आहे?")) {
            try {
                const userRef = ref(db, `ekamwatapzp/SocietyRegistrations/${id}`);
                await update(userRef, { isDeleted: true }); // Soft delete or actual remove as per logic
                toast.success("डेटा यशस्वीरित्या डिलीट केला!");
            } catch (error) {
                console.error("Delete Error:", error);
                toast.error("डेटा डिलीट करताना त्रुटी आली.");
            }
        }
    };

    // Filter out logically deleted records if you use soft delete
    const visibleData = filteredData.filter(item => !item.isDeleted);

    return (
        <InchargeLayout>
            <div className="min-h-screen bg-gray-50 flex flex-col  relative">
                {/* Header Section */}
                <div className="px-6 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <h1 className="text-xl font-medium text-gray-700">नवीन मजूर संस्थांची यादी</h1>
                    <div className="flex items-center text-sm font-medium text-gray-500 gap-2">
                        <span>सांगली जिल्हा परिषद</span>
                        <span>/</span>
                        <span className="text-gray-600 font-semibold">नवीन मजूर संस्थांची यादी</span>
                    </div>
                </div>

                <div className="px-6 pb-12">
                    <div className="max-w-[100%] mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-6">

                        {/* Status & Legend Header */}
                        <div className="flex flex-col gap-4 mb-6 text-right">
                            <div className="flex items-center justify-end gap-6 text-[13px] font-bold text-gray-700">
                                <div className="flex items-center gap-2">
                                    <span className="w-3.5 h-3.5 rounded-full bg-amber-500"></span>
                                    <span>निलंबित</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="w-3.5 h-3.5 rounded-full bg-emerald-600"></span>
                                    <span>मंजूर</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="w-3.5 h-3.5 rounded-full bg-rose-600"></span>
                                    <span>नाकारले</span>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2 text-[12px] font-bold text-gray-600 mt-2">
                                <div className="flex items-center gap-1.5 opacity-80">
                                    <FaUserTie className="text-gray-500" /> <span>अर्जदाराची संपूर्ण माहिती</span>
                                </div>
                                <div className="flex items-center gap-1.5 opacity-80">
                                    <FaFileAlt className="text-gray-500" /> <span>अर्जदाराची कागदपत्रे</span>
                                </div>
                                <div className="flex items-center gap-1.5 opacity-80">
                                    <FaTrash className="text-gray-500" /> <span>डिलीट</span>
                                </div>
                            </div>
                        </div>

                        {/* Search & Buttons */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                            <div className="flex items-center rounded overflow-hidden shadow-sm border border-gray-300 h-9">
                                <button className="bg-[#6c757d] hover:bg-[#5a6268] text-white px-4 text-xs font-medium border-r border-gray-400 h-full">Copy</button>
                                <button className="bg-[#6c757d] hover:bg-[#5a6268] text-white px-4 text-xs font-medium border-r border-gray-400 h-full">Excel</button>
                                <button className="bg-[#6c757d] hover:bg-[#5a6268] text-white px-4 text-xs font-medium h-full">Print</button>
                            </div>

                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <label>Search:</label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="border border-gray-300 rounded px-2 py-1 outline-none w-48 font-normal"
                                />
                            </div>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto border-t border-b border-gray-200 bg-[#f8f9fa]">
                            <table className="w-full text-left text-[14px] border-collapse bg-[#f8f9fa]">
                                <thead>
                                    <tr className="text-gray-600 font-normal border-b border-gray-200">
                                        <th className="px-4 py-3 border-r border-gray-200 w-16 text-center font-normal relative">
                                            क्र.
                                            <span className="absolute right-2 text-[10px] text-gray-400 opacity-50 flex flex-col top-1/2 -translate-y-1/2">
                                                <span>↑</span><span className="-mt-1">↓</span>
                                            </span>
                                        </th>
                                        <th className="px-4 py-3 border-r border-gray-200 min-w-[200px] font-normal relative">
                                            संस्थेचे नाव
                                            <span className="absolute right-2 text-[10px] text-gray-400 opacity-50 flex flex-col top-1/2 -translate-y-1/2">
                                                <span>↑</span><span className="-mt-1">↓</span>
                                            </span>
                                        </th>
                                        <th className="px-4 py-3 border-r border-gray-200 font-normal relative">
                                            फोन क्र.
                                            <span className="absolute right-2 text-[10px] text-gray-400 opacity-50 flex flex-col top-1/2 -translate-y-1/2">
                                                <span>↑</span><span className="-mt-1">↓</span>
                                            </span>
                                        </th>
                                        <th className="px-4 py-3 border-r border-gray-200 font-normal relative">
                                            ट्रा-झॅक्शन आयडी
                                            <span className="absolute right-2 text-[10px] text-gray-400 opacity-50 flex flex-col top-1/2 -translate-y-1/2">
                                                <span>↑</span><span className="-mt-1">↓</span>
                                            </span>
                                        </th>
                                        <th className="px-4 py-3 border-r border-gray-200 font-normal relative">
                                            रक्कम
                                            <span className="absolute right-2 text-[10px] text-gray-400 opacity-50 flex flex-col top-1/2 -translate-y-1/2">
                                                <span>↑</span><span className="-mt-1">↓</span>
                                            </span>
                                        </th>
                                        <th className="px-4 py-3 border-r border-gray-200 font-normal relative">
                                            अकाउंटंट फी चलन स्थिती
                                            <span className="absolute right-2 text-[10px] text-gray-400 opacity-50 flex flex-col top-1/2 -translate-y-1/2">
                                                <span>↑</span><span className="-mt-1">↓</span>
                                            </span>
                                        </th>
                                        <th className="px-4 py-3 text-center border-r border-gray-200 font-normal relative min-w-[150px]">
                                            चलन अपडेट करा<br />/ पहा
                                            <span className="absolute right-2 text-[10px] text-gray-400 opacity-50 flex flex-col top-1/2 -translate-y-1/2">
                                                <span>↑</span><span className="-mt-1">↓</span>
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-[#f8f9fa]">
                                    {loading ? (
                                        <tr><td colSpan="7" className="text-center py-5 text-gray-500 text-sm">Loading...</td></tr>
                                    ) : visibleData.length > 0 ? (
                                        visibleData.map((item, index) => (
                                            <tr key={item.id} className="hover:bg-gray-100 transition-colors">
                                                <td className="px-4 py-4 border-r border-gray-200 text-center text-sm text-gray-600">{index + 1}</td>
                                                <td className="px-4 py-4 border-r border-gray-200 text-sm text-[#007bff] hover:underline cursor-pointer">
                                                    {item.societyName || item.firstName}
                                                </td>
                                                <td className="px-4 py-4 border-r border-gray-200 text-sm text-gray-600">{item.chairmanWhatsapp || item.whatsappNo}</td>
                                                <td className="px-4 py-4 border-r border-gray-200 text-sm text-gray-600">{(item.transactionId || '').toUpperCase()}</td>
                                                <td className="px-4 py-4 border-r border-gray-200 text-sm text-gray-600">{item.amount ? `${item.amount}` : ''}</td>
                                                <td className={`px-4 py-4 border-r border-gray-200 text-sm font-medium ${getStatusColor(item.assistantStatus)}`}>
                                                    {item.assistantStatus || 'Pending'}
                                                </td>
                                                <td className="px-4 py-4 border-r border-gray-200">
                                                    <div className="flex items-center justify-center gap-4">
                                                        <button
                                                            className="text-gray-400 hover:text-blue-500 transition-colors"
                                                            title="अर्जदाराची संपूर्ण माहिती"
                                                        >
                                                            <FaUserTie size={15} />
                                                        </button>
                                                        <button
                                                            onClick={() => openModal(item)}
                                                            className="text-gray-400 hover:text-emerald-500 transition-colors"
                                                            title="अर्जदाराची कागदपत्रे"
                                                        >
                                                            <FaFileAlt size={15} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item.id)}
                                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                                            title="डिलीट"
                                                        >
                                                            <FaTrash size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="7" className="text-center py-6 text-sm text-gray-600">No data available in table</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        
                        {/* Pagination Footer */}
                        <div className="mt-4 flex flex-col md:flex-row items-center justify-between text-[14px] text-gray-600">
                            <div>
                                {visibleData.length > 0 
                                    ? `Showing 1 to ${visibleData.length} of ${visibleData.length} entries` 
                                    : 'Showing 0 to 0 of 0 entries'}
                            </div>
                            <div className="flex items-center mt-4 md:mt-0 shadow-sm rounded overflow-hidden">
                                <button className="px-3 py-1.5 border border-gray-300 text-gray-400 bg-white hover:bg-gray-50 transition-colors cursor-not-allowed text-sm">Previous</button>
                                <button className="px-3 py-1.5 border border-l-0 border-gray-300 text-gray-400 bg-white hover:bg-gray-50 transition-colors cursor-not-allowed text-sm">Next</button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* MODAL WINDOW FOR STATUS UPDATE */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
                        <div
                            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                            onClick={closeModal}
                        ></div>

                        <div className="relative bg-white w-full max-w-md rounded shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 ">
                                <h2 className="text-xl font-bold text-gray-800">Update Fee Challan Status</h2>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <div className="p-8 space-y-6">
                                <div className="text-xl font-bold text-blue-500 uppercase">
                                    {selectedUser?.societyName || `${selectedUser?.firstName} ${selectedUser?.surname}`}
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[15px] font-bold text-gray-700 italic flex items-center gap-1">
                                        Status <span className="text-red-500 font-bold">*</span>
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={updateStatus}
                                            onChange={(e) => setUpdateStatus(e.target.value)}
                                            className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-700 outline-none appearance-none cursor-pointer focus:ring-2 focus:ring-blue-500/20 italic font-medium"
                                        >
                                            <option value="">Select Status</option>
                                            <option value="Approved">Approved</option>
                                            <option value="Rejected">Rejected</option>
                                        </select>
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center gap-3">
                                <button
                                    onClick={closeModal}
                                    className="px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-md hover:bg-gray-600 transition transform active:scale-95 italic"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={handleUpdateStatus}
                                    disabled={isUpdating}
                                    className="px-6 py-2 bg-[#5c6dec] text-white rounded text-sm font-bold shadow-md hover:bg-blue-700 transition transform active:scale-95 italic min-w-[100px]"
                                >
                                    {isUpdating ? 'Updating...' : 'Update'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </InchargeLayout>
    );
};

export default ShowSocChallan;
